/* ============================================================================
   M3 — Rate Cards (RBAC-gated) — React port of mockup js/m3.js.
   One shared page parameterised by vertical:
     /supply/rate-cards/d2c  (D2C)   ·   /supply/rate-cards/ptl  (PTL)
   Features (mirrors m3.js):
     · Courier-code selector (codes for the vertical)
     · Zone × weight-slab SupplyTable (the m3.js .tbl view)
     · Deep-link ?code=<CODE> — context chip + guard when no rate card on file
     · Version-history dropdown + timeline — historical versions are read-only
     · Amber inline warning for rows with no linked agreement + [Link Agreement]
     · Edit / Link / Import are PROSHIPADMIN-only
     · Rate-card import via FileDrop → validation preview
   Frontend-only, dummy data, no network.
   ========================================================================== */
import React, {useMemo, useReducer, useState} from 'react'
import PropTypes from 'prop-types'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import {useNavigate, useSearchParams} from 'react-router-dom'
import PageTitle from '../../../components/PageTitle'
import {
  PageHeader,
  Badge,
  SupplyTable,
  FileDrop,
  Modal,
  SideDrawer,
  SearchSelect,
  useToast,
  inr,
  fmtDate,
  agreementShort,
  agreementLabel,
} from '../components'
import {
  DUMMY_CODES,
  DUMMY_AGREEMENTS,
  DUMMY_RATECARDS,
  RATECARD_HISTORY,
  RATECARD_HISTORICAL_ROWS,
} from '../dummyData'

/* this prototype runs as a full ProShip admin (mockup PS.ROLE = PROSHIPADMIN). */
const IS_ADMIN = true

const TEMPLATE_CSV =
  'courier_code,vertical,zone,weight_slab,rate_inr,effective_from,linked_agreement_id\n' +
  'Delhivery_ENT_SURFACE,D2C,Zone 1,0–0.5 kg,32,2026-06-01,a2\n' +
  'Delhivery_ENT_SURFACE,D2C,Zone 1,0.5–1 kg,46,2026-06-01,a2\n' +
  'Delhivery_ENT_SURFACE,D2C,Zone 3,0–0.5 kg,49,2026-06-01,a2\n'

/* ───────── data helpers (port of m3.js) ───────── */
const codesForVertical = (vert) => DUMMY_CODES.filter((c) => c.vertical === vert)

const agreementsForCode = (code) => {
  if (!code) return []
  return DUMMY_AGREEMENTS.filter(
    (a) =>
      a.parentId === code.parentId &&
      (a.appliesToAll || (a.linkedCodes || []).indexOf(code.code) >= 0)
  )
}

const currentRows = (code, vert, overlay) =>
  (overlay[code] || DUMMY_RATECARDS).filter((r) => r.code === code && r.vertical === vert)

const versionRows = (code, vert, version, overlay) => {
  const hist = RATECARD_HISTORICAL_ROWS[code + '|' + version]
  if (hist) return hist
  return currentRows(code, vert, overlay)
}

const historyFor = (code) => (RATECARD_HISTORY[code] || []).slice()

const currentVersionOf = (code) => {
  const rows = DUMMY_RATECARDS.filter((r) => r.code === code)
  if (rows.length) return rows[0].version
  const h = historyFor(code)[0]
  return h ? h.version : 1
}

const uniq = (rows, key) => {
  const seen = {}
  const out = []
  rows.forEach((r) => {
    if (!seen[r[key]]) {
      seen[r[key]] = 1
      out.push(r[key])
    }
  })
  return out
}

/* Linked-agreement cell — React port of PS.agreementCell. */
function AgreementCell({id}) {
  if (!id)
    return (
      <span className='mini-pill mp-amber'>
        <WarningAmberOutlinedIcon
          style={{width: 11, height: 11, verticalAlign: '-1.5px', marginRight: 3}}
        />
        Unlinked
      </span>
    )
  return (
    <span className='tag' title={agreementLabel(id)}>
      <DescriptionOutlinedIcon style={{width: 12, height: 12}} /> {agreementShort(id)}
    </span>
  )
}
AgreementCell.propTypes = {id: PropTypes.string}

/* Deep-link context chip — React port of m3.js renderContextChip. */
function ContextChip({label, onClear}) {
  return (
    <div className='warnbox info' style={{marginBottom: 16, alignItems: 'center'}}>
      <InfoOutlinedIcon style={{width: 16, height: 16}} />
      <div style={{flex: 1}}>
        {label}{' '}
        {onClear ? (
          <button
            type='button'
            className='lnk'
            style={{background: 'none', border: 0, padding: 0, font: 'inherit', marginLeft: 6}}
            onClick={onClear}
          >
            Clear / view all
          </button>
        ) : null}
      </div>
    </div>
  )
}
ContextChip.propTypes = {label: PropTypes.string, onClear: PropTypes.func}

/* ════════════════════ PAGE ════════════════════ */
export default function RateCards({vertical = 'D2C'}) {
  const vert = vertical === 'PTL' ? 'PTL' : 'D2C'
  const codes = useMemo(() => codesForVertical(vert), [vert])
  const toast = useToast()
  const navigate = useNavigate()

  // local edit overlay: code -> mutated current rows (Edit Rate / Link Agreement)
  const [overlay, dispatch] = useReducer(overlayReducer, {})

  // default selection: first code that has a rate card, else first code
  const defaultCode = useMemo(() => {
    const withRc = codes.find((c) =>
      DUMMY_RATECARDS.some((r) => r.code === c.code && r.vertical === vert)
    )
    return (withRc || codes[0] || {}).code || null
  }, [codes, vert])

  const [selCode, setSelCode] = useState(defaultCode)
  const [version, setVersion] = useState('current')
  const [importOpen, setImportOpen] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [editRow, setEditRow] = useState(null)

  // deep-link: ?code=<CODE> pins the selection when it belongs to this vertical
  const [params, setParams] = useSearchParams()
  const focusCode = params.get('code')
  const focusValid = !!(focusCode && codes.some((c) => c.code === focusCode))
  React.useEffect(() => {
    if (focusValid) {
      setSelCode(focusCode)
      setVersion('current')
    }
  }, [focusValid, focusCode])
  const clearFocus = () => {
    const next = new URLSearchParams(params)
    next.delete('code')
    setParams(next, {replace: true})
  }

  // keep selection valid if vertical changes; ?code= override wins
  const code = focusValid
    ? focusCode
    : codes.some((c) => c.code === selCode)
    ? selCode
    : defaultCode

  // ── empty: no codes for this vertical ──
  if (!codes.length) {
    return (
      <>
        <PageTitle>{`${vert} Rate Cards — ProShip`}</PageTitle>
        <PageHeader
          title={`${vert} Rate Cards`}
          sub={`Rate cards for ${vert} courier codes by zone and weight slab.`}
        />
        <div className='empty'>
          <div className='ic'>
            <PaymentsOutlinedIcon style={{width: 30, height: 30}} />
          </div>
          <h3>No {vert} courier codes</h3>
          <p>
            There are no {vert} courier codes set up yet. Add a code in Courier Codes to capture its
            rate card.
          </p>
          <button
            className='btn btn-sm'
            style={{marginTop: 8}}
            onClick={() => navigate('/supply/courier-codes')}
          >
            Go to Courier Codes
          </button>
        </div>
      </>
    )
  }

  // ── deep-link guard: ?code= points at a code with no rate card on file ──
  if (focusValid && !currentRows(focusCode, vert, overlay).length) {
    return (
      <>
        <PageTitle>{`${vert} Rate Cards — ProShip`}</PageTitle>
        <PageHeader
          title={`${vert} Rate Cards`}
          sub={`Zone × weight-slab rates for ${vert} courier codes.`}
        />
        <ContextChip label={'Viewing commercials for ' + focusCode} onClear={clearFocus} />
        <div className='empty'>
          <div className='ic'>
            <PaymentsOutlinedIcon style={{width: 30, height: 30}} />
          </div>
          <h3>No rate card configured yet for {focusCode}</h3>
          <p>This courier code does not have a rate card on file. A ProShip admin can import one.</p>
          {IS_ADMIN ? (
            <button
              className='btn btn-sm btn-primary'
              style={{marginTop: 8}}
              onClick={() => setImportOpen(true)}
            >
              <UploadOutlinedIcon style={{width: 14, height: 14}} />
              Add rate card
            </button>
          ) : null}
        </div>
        <ImportDrawer
          open={importOpen}
          vert={vert}
          codes={codes}
          defaultCode={focusCode}
          onClose={() => setImportOpen(false)}
          onImport={(count) => {
            setImportOpen(false)
            toast(
              'Imported ' +
                count +
                ' rate row(s) for ' +
                vert +
                '. (Production target: validated server-side ingest.)',
              'success'
            )
          }}
        />
      </>
    )
  }

  const codeObj = DUMMY_CODES.find((c) => c.code === code)
  const isHistorical = version !== 'current'
  const hist = historyFor(code)
  const curV = currentVersionOf(code)
  const rows = isHistorical
    ? versionRows(code, vert, version, overlay)
    : currentRows(code, vert, overlay)
  const vNum = isHistorical ? version : curV
  const hmeta = hist.find((h) => String(h.version) === String(vNum))
  const unlinked = rows.filter((r) => !r.linkedAgreement)

  const selectCode = (next) => {
    if (focusValid) clearFocus() // manual change releases the ?code= pin
    setSelCode(next)
    setVersion('current')
  }

  return (
    <>
      <PageTitle>{`${vert} Rate Cards — ProShip`}</PageTitle>

      <PageHeader
        title={
          <>
            {vert} Rate Cards{' '}
            <span
              className={'chip chip-' + vert.toLowerCase()}
              style={{verticalAlign: 'middle', fontSize: 12, fontWeight: 700, marginLeft: 6}}
            >
              {vert} Freight
            </span>
          </>
        }
        sub={`Zone × weight-slab rates for ${vert} courier codes, linked to the governing agreement.`}
        actions={
          IS_ADMIN ? (
            <button className='btn' onClick={() => setImportOpen(true)}>
              <UploadOutlinedIcon style={{width: 16, height: 16}} />
              Import Rate Card
            </button>
          ) : null
        }
      />

      {/* code + version selector */}
      <div className='card card-pad' style={{marginBottom: 16}}>
        <div
          className='row gap16 wrap'
          style={{display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap'}}
        >
          <div className='field' style={{margin: 0, minWidth: 320, flex: 1}}>
            <label>
              Courier code <span className='req'>*</span>
            </label>
            <SearchSelect
              value={code || ''}
              options={codes.map((c) => {
                const n = DUMMY_RATECARDS.filter(
                  (r) => r.code === c.code && r.vertical === vert
                ).length
                return {value: c.code, label: c.display + ' (' + c.code + ')' + (n ? '' : ' — no rate card')}
              })}
              onChange={(v) => selectCode(v)}
            />
            <div className='help'>
              <InfoOutlinedIcon style={{width: 13, height: 13}} />
              Pick a {vert} courier code to view its rate card. Only {vert} codes are shown.
            </div>
          </div>

          <div className='field' style={{margin: 0, minWidth: 260}}>
            <label>Version history</label>
            <SearchSelect
              value={String(version)}
              options={[
                {value: 'current', label: 'v' + curV + ' — Current'},
                ...hist
                  .filter((h) => h.version !== curV)
                  .map((h) => {
                    const who = String(h.createdBy || '').split('@')[0]
                    return {value: String(h.version), label: 'v' + h.version + ' — ' + fmtDate(h.effectiveFrom) + ' (by ' + who + ')'}
                  }),
              ]}
              onChange={(v) => setVersion(v)}
            />
            <div className='help'>
              <HistoryOutlinedIcon style={{width: 13, height: 13}} />
              Select an older version to view it read-only.
            </div>
          </div>
        </div>
      </div>

      {/* deep-link context chip */}
      {focusValid ? (
        <ContextChip label={'Viewing commercials for ' + focusCode} onClear={clearFocus} />
      ) : null}

      {/* historical read-only banner */}
      {isHistorical ? (
        <div className='warnbox info' style={{marginBottom: 16}}>
          <HistoryOutlinedIcon style={{width: 18, height: 18}} />
          <div>
            Viewing historical version — not the current rate card.{' '}
            <button
              type='button'
              className='lnk'
              style={{background: 'none', border: 0, padding: 0, font: 'inherit'}}
              onClick={() => setVersion('current')}
            >
              View Current
            </button>
          </div>
        </div>
      ) : null}

      {/* version meta */}
      <div
        className='row center wrap'
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <div className='seclabel'>
          Rate card · v{vNum}
          {isHistorical ? ' (read-only)' : ''}
        </div>
        {hmeta ? (
          <span className='muted' style={{fontSize: 12, marginLeft: 'auto'}}>
            Effective {fmtDate(hmeta.effectiveFrom)} · created by {hmeta.createdBy} on{' '}
            {fmtDate(hmeta.createdAt)}
            {hmeta.note ? ' · ' + hmeta.note : ''}
          </span>
        ) : null}
      </div>

      {/* amber inline warning — any row with no linked agreement */}
      {unlinked.length ? (
        <div className='warnbox' style={{marginBottom: 16}}>
          <WarningAmberOutlinedIcon style={{width: 18, height: 18}} />
          <div>
            <b>No agreement linked to this rate card.</b>
            <br />
            The commercials in this rate card are not backed by a signed agreement in the
            repository.
            <div className='muted' style={{marginTop: 4}}>
              {unlinked.length} row(s) affected.
            </div>
            {!isHistorical ? (
              IS_ADMIN ? (
                <button
                  className='btn btn-sm'
                  style={{marginTop: 12}}
                  onClick={() => setLinkOpen(true)}
                >
                  <LinkOutlinedIcon />
                  Link Agreement
                </button>
              ) : (
                <span className='tip' title='Editing rate cards is restricted to ProShip admins.'>
                  <button className='btn btn-sm' disabled style={{marginTop: 12}}>
                    <LockOutlinedIcon />
                    Link Agreement
                  </button>
                </span>
              )
            ) : null}
          </div>
        </div>
      ) : null}

      {/* zone × slab table (m3.js .tbl view) */}
      <SupplyTable
        key={'rc_' + vert + '_' + code + '_' + version}
        searchKeys={['zone', 'slab']}
        searchPlaceholder='Search by zone or weight slab...'
        exportName={'rate-card-' + vert.toLowerCase() + '-' + code}
        emptyText={
          'No rate card rows captured for this code yet.' +
          (IS_ADMIN
            ? ' Use Import Rate Card above to load them.'
            : ' A ProShip admin can import them.')
        }
        defaultSort={{key: 'zone', dir: 'asc'}}
        pageSize={20}
        rowKey={(r) => r.code + '|' + r.zone + '|' + r.slab + '|' + r.version}
        rows={rows}
        filters={[
          {
            key: 'zone',
            label: 'Zone',
            type: 'multi',
            options: uniq(rows, 'zone').map((z) => ({value: z, label: z})),
            predicate: (r, selF) => selF.has(r.zone),
          },
        ]}
        columns={[
          {key: 'zone', label: 'Zone'},
          {key: 'slab', label: 'Weight Slab', sortable: false},
          {
            key: 'rate',
            label: 'Rate',
            align: 'right',
            render: (r) => <b className='mono'>{inr(r.rate)}</b>,
            exportValue: (r) => r.rate,
          },
          {
            key: 'effectiveFrom',
            label: 'Effective Date',
            render: (r) => fmtDate(r.effectiveFrom),
            value: (r) => r.effectiveFrom,
          },
          {
            key: 'linkedAgreement',
            label: 'Linked Agreement',
            sortable: false,
            render: (r) => <AgreementCell id={r.linkedAgreement} />,
            exportValue: (r) => r.linkedAgreement || 'unlinked',
          },
        ]}
        rowActions={
          IS_ADMIN
            ? (r) =>
                [
                  {
                    label: 'View linked agreement',
                    icon: <VisibilityOutlinedIcon fontSize='small' />,
                    hidden: !r.linkedAgreement,
                    onClick: () => navigate('/supply/agreements'),
                  },
                  {
                    label: isHistorical ? 'Edit (current only)' : 'Edit rate',
                    icon: <EditOutlinedIcon fontSize='small' />,
                    onClick: () => {
                      if (isHistorical) {
                        toast(
                          'Historical versions are read-only. Switch to the current version to edit.',
                          'info'
                        )
                        return
                      }
                      setEditRow(r)
                    },
                  },
                  !r.linkedAgreement && !isHistorical
                    ? {
                        label: 'Link agreement',
                        icon: <LinkOutlinedIcon fontSize='small' />,
                        onClick: () => setLinkOpen(true),
                      }
                    : null,
                ].filter(Boolean)
            : undefined
        }
      />

      {/* version-history timeline */}
      <div className='seclabel' style={{margin: '20px 0 12px'}}>
        <HistoryOutlinedIcon
          style={{width: 14, height: 14, verticalAlign: '-2px', marginRight: 4}}
        />
        Version history
      </div>
      {hist.length ? (
        <div className='timeline'>
          {hist.map((h) => {
            const isCur = h.version === curV
            const isViewing = String(version) === String(h.version) || (isCur && !isHistorical)
            return (
              <div className='tl-item' key={h.version}>
                <div className='tl-card'>
                  <div
                    className='row center wrap'
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <div
                      className='row center gap8'
                      style={{display: 'flex', alignItems: 'center', gap: 8}}
                    >
                      <b>v{h.version}</b>
                      {isCur ? <Badge status='active' label='Current' /> : null}
                      {agreementShort(h.linkedAgreement) ? (
                        <span className='tag'>{agreementShort(h.linkedAgreement)}</span>
                      ) : (
                        <span className='mini-pill mp-amber'>Unlinked</span>
                      )}
                    </div>
                    {isViewing ? (
                      <span className='muted' style={{fontSize: 12, marginLeft: 'auto'}}>
                        Now viewing
                      </span>
                    ) : (
                      <button
                        type='button'
                        className='lnk'
                        style={{
                          background: 'none',
                          border: 0,
                          padding: 0,
                          font: 'inherit',
                          marginLeft: 'auto',
                        }}
                        onClick={() => setVersion(isCur ? 'current' : String(h.version))}
                      >
                        View this version
                      </button>
                    )}
                  </div>
                  <div className='muted' style={{fontSize: 12.5, marginTop: 4}}>
                    Effective {fmtDate(h.effectiveFrom)} · created by {h.createdBy} on{' '}
                    {fmtDate(h.createdAt)}
                  </div>
                  {h.note ? (
                    <div style={{fontSize: 13, marginTop: 4, color: 'var(--ink-2)'}}>{h.note}</div>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className='muted' style={{fontSize: 13}}>
          No earlier versions recorded for this code.
        </div>
      )}

      {/* ───────── Edit rate modal ───────── */}
      <EditRateModal
        row={editRow}
        onClose={() => setEditRow(null)}
        onSave={(row, val) => {
          dispatch({type: 'edit', code, vert, row, rate: val})
          toast(
            'Rate updated to ' +
              inr(val) +
              ' for ' +
              row.zone +
              ' · ' +
              row.slab +
              '. Audit recorded.',
            'success'
          )
          setEditRow(null)
        }}
      />

      {/* ───────── Link agreement drawer ───────── */}
      <LinkAgreementDrawer
        open={linkOpen}
        code={codeObj}
        onClose={() => setLinkOpen(false)}
        onLink={(agrId) => {
          const n = currentRows(code, vert, overlay).filter((r) => !r.linkedAgreement).length
          dispatch({type: 'link', code, vert, agrId})
          setLinkOpen(false)
          toast(
            n +
              ' rate row' +
              (n !== 1 ? 's' : '') +
              ' linked to ' +
              agreementShort(agrId) +
              '. Audit recorded.',
            'success'
          )
        }}
      />

      {/* ───────── Import drawer ───────── */}
      <ImportDrawer
        open={importOpen}
        vert={vert}
        codes={codes}
        defaultCode={code}
        onClose={() => setImportOpen(false)}
        onImport={(count) => {
          setImportOpen(false)
          toast(
            'Imported ' +
              count +
              ' rate row(s) for ' +
              vert +
              '. (Production target: validated server-side ingest.)',
            'success'
          )
        }}
      />
    </>
  )
}

RateCards.propTypes = {
  vertical: PropTypes.string,
}

/* in-memory mutation overlay so Edit/Link persist while the page is mounted */
function overlayReducer(state, action) {
  const base = state[action.code] || DUMMY_RATECARDS.map((r) => ({...r}))
  if (action.type === 'edit') {
    const next = base.map((r) =>
      r.code === action.code &&
      r.vertical === action.vert &&
      r.zone === action.row.zone &&
      r.slab === action.row.slab
        ? {...r, rate: action.rate}
        : r
    )
    return {...state, [action.code]: next}
  }
  if (action.type === 'link') {
    const next = base.map((r) =>
      r.code === action.code && r.vertical === action.vert && !r.linkedAgreement
        ? {...r, linkedAgreement: action.agrId}
        : r
    )
    return {...state, [action.code]: next}
  }
  return state
}

/* ════════════════════ Edit rate modal ════════════════════ */
function EditRateModal({row, onClose, onSave}) {
  const [val, setVal] = useState('')
  const [touched, setTouched] = useState(false)
  React.useEffect(() => {
    if (row) {
      setVal(String(row.rate))
      setTouched(false)
    }
  }, [row])
  if (!row) return null
  const num = parseFloat(val)
  const invalid = !(num > 0)
  return (
    <Modal
      open={!!row}
      onClose={onClose}
      title={'Edit rate — ' + row.zone + ' · ' + row.slab}
      icon={<EditOutlinedIcon fontSize='small' />}
      actions={[
        {label: 'Cancel', onClick: onClose},
        {
          label: 'Save',
          primary: true,
          onClick: () => {
            setTouched(true)
            if (invalid) return false
            onSave(row, num)
          },
        },
      ]}
    >
      <div className='dl' style={{maxWidth: 480, marginBottom: 16}}>
        <dt>Courier code</dt>
        <dd>
          <span className='mono'>{row.code}</span>
        </dd>
        <dt>Zone</dt>
        <dd>{row.zone}</dd>
        <dt>Weight slab</dt>
        <dd>{row.slab}</dd>
      </div>
      <div className={'field' + (touched && invalid ? ' invalid' : '')} style={{margin: 0}}>
        <label>
          Rate (₹) <span className='req'>*</span>
        </label>
        <input
          className={'input' + (touched && invalid ? ' err' : '')}
          type='number'
          min='0'
          step='1'
          value={val}
          placeholder='e.g. 46'
          onChange={(e) => setVal(e.target.value)}
        />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Per-shipment rate for this zone and slab. Saving updates the current rate card version.
        </div>
        <div className='vmsg'>Enter a rate greater than zero.</div>
      </div>
    </Modal>
  )
}
EditRateModal.propTypes = {
  row: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
}

/* ════════════════════ Link agreement drawer ════════════════════ */
function LinkAgreementDrawer({open, code, onClose, onLink}) {
  const [sel, setSel] = useState(null)
  const [err, setErr] = useState(false)
  React.useEffect(() => {
    if (open) {
      setSel(null)
      setErr(false)
    }
  }, [open])

  let candidates = agreementsForCode(code)
  if (!candidates.length && code) {
    candidates = DUMMY_AGREEMENTS.filter((a) => a.parentId === code.parentId)
  }
  candidates = candidates
    .slice()
    .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title='Link Agreement'
      footer={
        <>
          <button className='btn btn-ghost' onClick={onClose}>
            Cancel
          </button>
          {candidates.length ? (
            <button
              className='btn btn-primary'
              onClick={() => {
                if (!sel) {
                  setErr(true)
                  return
                }
                onLink(sel)
              }}
            >
              <LinkOutlinedIcon style={{width: 16, height: 16}} />
              Link Agreement
            </button>
          ) : null}
        </>
      }
    >
      {!candidates.length ? (
        <div className='warnbox err'>
          <ErrorOutlineIcon />
          <div>
            No agreements found for {code ? code.parent : 'this partner'}. Upload one in the
            Agreement Repository first.
          </div>
        </div>
      ) : (
        <>
          <p className='muted' style={{fontSize: 13, margin: '0 0 16px'}}>
            Select the signed agreement that governs the commercials in this rate card. Only
            agreements for <b>{code ? code.parent : ''}</b> are shown.
          </p>
          <div className={'field' + (err ? ' invalid' : '')} style={{margin: 0}}>
            <label>
              Agreement <span className='req'>*</span>
            </label>
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 6,
                maxHeight: 320,
                overflow: 'auto',
                padding: 6,
              }}
            >
              {candidates.map((a) => (
                <label
                  className='checkrow optcard'
                  key={a.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 6,
                    alignItems: 'flex-start',
                  }}
                >
                  <input
                    type='radio'
                    name='rcAgr'
                    value={a.id}
                    checked={sel === a.id}
                    onChange={() => {
                      setSel(a.id)
                      setErr(false)
                    }}
                    style={{marginTop: 3}}
                  />
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      {a.type === 'Commercial Addendum' ? 'Addendum #' + a.addendumNo : a.type}
                      <Badge
                        status={a.stage === 2 ? 'signed' : 'pending'}
                        label={a.stage === 2 ? 'Stage 2: Signed' : 'Stage 1: Email received'}
                      />
                    </div>
                    <div style={{fontSize: 12.5, color: 'var(--ink-2)'}}>{a.title}</div>
                    <div className='muted' style={{fontSize: 11.5, marginTop: 2}}>
                      Effective {fmtDate(a.effectiveFrom)} ·{' '}
                      {a.appliesToAll ? 'all codes' : 'specific codes'} · uploaded by {a.uploadedBy}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className='vmsg'>Select an agreement to link.</div>
          </div>
        </>
      )}
    </SideDrawer>
  )
}
LinkAgreementDrawer.propTypes = {
  open: PropTypes.bool,
  code: PropTypes.object,
  onClose: PropTypes.func,
  onLink: PropTypes.func,
}

/* ════════════════════ Import drawer ════════════════════ */
function parseDemoCsv(text) {
  const lines = String(text).trim().split(/\r?\n/)
  const head = lines.shift().split(',')
  return lines.map((ln) => {
    const cells = ln.split(',')
    const o = {}
    head.forEach((h, i) => (o[h.trim()] = (cells[i] || '').trim()))
    return o
  })
}

function ImportDrawer({open, vert, codes, defaultCode, onClose, onImport}) {
  const [target, setTarget] = useState(defaultCode)
  const [preview, setPreview] = useState(null)
  React.useEffect(() => {
    if (open) {
      setTarget(defaultCode)
      setPreview(null)
    }
  }, [open, defaultCode])

  const onFile = (file) => {
    const demo = parseDemoCsv(TEMPLATE_CSV)
    const bad = demo.filter((r) => !r.linked_agreement_id).length
    setPreview({file, demo, bad})
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={'Import ' + vert + ' Rate Card'}
      width={560}
      footer={
        <button className='btn btn-ghost' onClick={onClose}>
          Close
        </button>
      }
    >
      <p className='muted' style={{fontSize: 13, margin: '0 0 16px'}}>
        Bulk-load rate rows from a CSV/XLSX. Rows map to courier code, zone, weight slab, rate,
        effective date and the linked agreement.
      </p>
      <div className='field'>
        <label>Target courier code</label>
        <SearchSelect
          value={target || ''}
          options={codes.map((c) => ({value: c.code, label: c.display + ' (' + c.code + ')'}))}
          onChange={(v) => setTarget(v)}
        />
        <div className='help'>
          <InfoOutlinedIcon style={{width: 13, height: 13}} />
          Default matches your current selection. The CSV courier_code column overrides per row.
        </div>
      </div>

      <FileDrop
        accept='.csv,.xlsx'
        maxMB={10}
        title='Drag & drop your rate card file'
        hint='Accepted: .csv, .xlsx · max 10 MB'
        sampleName='rate-card-template.csv'
        sampleLabel='Download template'
        sampleContent={TEMPLATE_CSV}
        onFile={onFile}
      />

      {preview ? (
        <div>
          <div className='seclabel' style={{margin: '16px 0 8px'}}>
            Validation preview
          </div>
          <div className={'warnbox ' + (preview.bad ? '' : 'ok')} style={{marginBottom: 12}}>
            {preview.bad ? <WarningAmberOutlinedIcon /> : <CheckCircleOutlineIcon />}
            <div>
              {preview.demo.length} row{preview.demo.length !== 1 ? 's' : ''} parsed from{' '}
              <b>{preview.file.name}</b>.{' '}
              {preview.bad
                ? preview.bad +
                  ' row(s) have no linked agreement and will show the unlinked warning.'
                : 'All rows have a linked agreement.'}
            </div>
          </div>
          <div className='tbl-wrap'>
            <table className='tbl'>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Zone</th>
                  <th>Slab</th>
                  <th style={{textAlign: 'right'}}>Rate</th>
                  <th>Effective</th>
                  <th>Agreement</th>
                </tr>
              </thead>
              <tbody>
                {preview.demo.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <span className='mono'>{r.courier_code}</span>
                    </td>
                    <td>{r.zone}</td>
                    <td>{r.weight_slab}</td>
                    <td style={{textAlign: 'right'}}>
                      <b className='mono'>{inr(+r.rate_inr)}</b>
                    </td>
                    <td>{fmtDate(r.effective_from)}</td>
                    <td>
                      {r.linked_agreement_id ? (
                        <span className='tag tag-mono'>{r.linked_agreement_id}</span>
                      ) : (
                        <span className='mini-pill mp-amber'>Unlinked</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            className='row gap12'
            style={{display: 'flex', gap: 12, marginTop: 14, justifyContent: 'flex-end'}}
          >
            <button className='btn btn-primary' onClick={() => onImport(preview.demo.length)}>
              <CheckIcon style={{width: 16, height: 16}} />
              Import {preview.demo.length} rows
            </button>
          </div>
        </div>
      ) : null}
    </SideDrawer>
  )
}
ImportDrawer.propTypes = {
  open: PropTypes.bool,
  vert: PropTypes.string,
  codes: PropTypes.array,
  defaultCode: PropTypes.string,
  onClose: PropTypes.func,
  onImport: PropTypes.func,
}
